import { useCallback, useEffect, useState } from 'react';
import { z } from 'zod';
import { type BBox, bboxSchema, type MapLocation, mapLocationSchema } from '@repo/shared';

/**
 * Every place the viewer has searched for, persisted in localStorage.
 *
 * Distinct from saved locations: that list is curated by hand and deliberately
 * short, this one is a passive log so a place looked at last week is still
 * reachable without retyping it. Like saved locations it is browser-only and
 * re-validated with Zod on the way out, because localStorage is user-writable
 * input that outlives any deploy.
 */

/** Versioned so a future shape change can be migrated rather than guessed at. */
const STORAGE_KEY = 'raydario.search-history.v1';

/**
 * The whole history is kept, but not without end: localStorage is a few
 * megabytes shared by the whole origin, and a write that overflows the quota
 * throws and loses the entry that triggered it. At roughly 150 bytes an entry
 * this ceiling costs ~75KB — far more history than anyone will page through,
 * and far less than the browser will refuse.
 */
const MAX_ENTRIES = 500;

/**
 * Rows per page. The list is newest-first, so the first page is the ten most
 * recent searches and the pager is what reaches everything older.
 */
export const HISTORY_PAGE_SIZE = 10;

export const searchHistoryEntrySchema = mapLocationSchema.extend({
  id: z.string().min(1),
  /** Epoch milliseconds of the most recent time this place was chosen. */
  searchedAt: z.number().int().nonnegative(),
  /** Present for areas — kept so replaying an entry restores the same framing. */
  bbox: bboxSchema.optional(),
});
export type SearchHistoryEntry = z.infer<typeof searchHistoryEntrySchema>;

const searchHistorySchema = z.array(searchHistoryEntrySchema);

/** What `record` accepts: a place, plus the extent a geocoded area carries. */
export interface SearchedPlace extends MapLocation {
  bbox?: BBox;
}

function read(): SearchHistoryEntry[] {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];

    const parsed = searchHistorySchema.safeParse(JSON.parse(raw));
    // Corrupt or outdated data is discarded rather than crashing the app.
    return parsed.success ? parsed.data : [];
  } catch {
    return [];
  }
}

function write(entries: SearchHistoryEntry[]): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(entries));
  } catch {
    // Private browsing or a full quota — history is a nicety, not a requirement.
  }
}

/** Two entries are "the same place" if they're within ~10m of each other. */
function isSamePlace(a: MapLocation, b: MapLocation): boolean {
  return Math.abs(a.longitude - b.longitude) < 1e-4 && Math.abs(a.latitude - b.latitude) < 1e-4;
}

/**
 * The places that have been searched for, newest first.
 *
 * Searching the same place twice moves it back to the top rather than adding a
 * second row: the history is a list of places, not of keystrokes, and someone
 * who checks their home town every morning would otherwise have a history made
 * of nothing else.
 */
export function useSearchHistory() {
  const [entries, setEntries] = useState<SearchHistoryEntry[]>([]);

  // Read after mount rather than during render: localStorage is unavailable
  // during SSR/tests and reading it in an initializer would be a side effect.
  useEffect(() => setEntries(read()), []);

  // Every mutation is written against the previous state rather than the
  // rendered `entries`, so two changes in the same tick can't drop one another.
  const update = useCallback(
    (next: (current: readonly SearchHistoryEntry[]) => SearchHistoryEntry[]) =>
      setEntries((current) => {
        const updated = next(current);
        write(updated);
        return updated;
      }),
    [],
  );

  const record = useCallback(
    (place: SearchedPlace) =>
      update((current) => {
        const entry: SearchHistoryEntry = {
          label: place.label,
          longitude: place.longitude,
          latitude: place.latitude,
          ...(place.bbox ? { bbox: place.bbox } : {}),
          id: crypto.randomUUID(),
          searchedAt: Date.now(),
        };
        return [entry, ...current.filter((seen) => !isSamePlace(seen, place))].slice(
          0,
          MAX_ENTRIES,
        );
      }),
    [update],
  );

  const remove = useCallback(
    (id: string) => update((current) => current.filter((entry) => entry.id !== id)),
    [update],
  );

  const clear = useCallback(() => update(() => []), [update]);

  return { entries, record, remove, clear };
}

/** Pages needed for `total` entries — at least one, so "page 1 of 1" reads sensibly. */
export function pageCount(total: number, pageSize: number = HISTORY_PAGE_SIZE): number {
  return Math.max(1, Math.ceil(total / pageSize));
}

/** The slice shown on a zero-based page index, clamped to the entries that exist. */
export function pageOf<T>(
  entries: readonly T[],
  page: number,
  pageSize: number = HISTORY_PAGE_SIZE,
): T[] {
  const clamped = Math.min(Math.max(page, 0), pageCount(entries.length, pageSize) - 1);
  const start = clamped * pageSize;
  return entries.slice(start, start + pageSize);
}
